/** Shared form → API payload mapping for agent Add Client and public self-screen. */

const COUNTRY_TITLES: Record<string, string> = {
  angola: 'Angola', belgium: 'Belgium', burundi: 'Burundi', cameroon: 'Cameroon',
  car: 'Central African Republic', chad: 'Chad', china: 'China',
  congo_rep: 'Congo (Republic)', drc: 'Congo (DRC)', egypt: 'Egypt',
  ethiopia: 'Ethiopia', france: 'France', germany: 'Germany', india: 'India',
  kenya: 'Kenya', nigeria: 'Nigeria', rwanda: 'Rwanda', south_sudan: 'South Sudan',
  sudan: 'Sudan', tanzania: 'Tanzania', uganda: 'Uganda', uk: 'United Kingdom',
  usa: 'United States', zambia: 'Zambia',
};

/** Combine separate country-code + local number into one E.164-ish phone for the API. */
export function combinePhoneFields(form: Record<string, unknown>): string {
  const ccRaw = String(form.phone_country_code ?? '').trim();
  const localRaw = String(form.phone_number ?? '').trim();
  const ccDigits = ccRaw.replace(/\D/g, '');
  const localDigits = localRaw.replace(/\D/g, '').replace(/^0+/, '');
  if (ccDigits && localDigits) return `+${ccDigits}${localDigits}`;
  if (localRaw.startsWith('+') || (localDigits.length >= 9 && !ccDigits)) {
    return localRaw.startsWith('+') ? `+${localDigits}` : `+${localDigits}`;
  }
  if (ccDigits && !localDigits) return `+${ccDigits}`;
  return localRaw || '';
}

const EXPOSURE_KEYS = [
  'ebola_contact_confirmed_case',
  'ebola_healthcare_lab_work',
  'ebola_funeral_body_prep',
  'ebola_contact_animals',
  'ebola_bushmeat_handling',
  'ebola_forest_cave_exposure',
] as const;

/**
 * Public /screen required-field check. Returns an i18n key for the first missing item, or null.
 */
export function validatePublicScreenForm(form: Record<string, unknown>): string | null {
  const s = (k: string) => String(form[k] ?? '').trim();
  if (!s('full_name')) return 'err_required';
  if (!s('age') || Number.isNaN(Number(s('age')))) return 'err_required';
  if (!s('sex')) return 'err_required';
  if (!s('nationality')) return 'err_required';
  if (!s('id_number')) return 'err_required';
  if (!s('phone_country_code') || !s('phone_number')) return 'err_phone';
  if (!combinePhoneFields(form)) return 'err_phone_valid';
  if (!s('ebola_country_of_departure')) return 'err_required';
  if (s('ebola_country_of_departure') === 'other' && !s('ebola_other_country_specify')) return 'err_required';
  const mode = s('ebola_mode_of_transport').toUpperCase();
  if (!['AIR', 'SHIP', 'ROAD'].includes(mode)) return 'err_required';
  if (!s('ebola_point_of_entry')) return 'err_required';
  if (mode === 'AIR' && !s('ebola_flight_number')) return 'err_required';
  if (mode === 'SHIP' && !s('ebola_vessel_number')) return 'err_required';
  if (mode === 'ROAD' && !s('ebola_vehicle_number')) return 'err_required';
  if ((mode === 'AIR' || mode === 'SHIP') && !s('ebola_seat_number')) return 'err_required';
  if (!s('ebola_visited_affected_countries')) return 'err_required';
  if (s('ebola_visited_affected_countries') === 'yes') {
    for (const key of EXPOSURE_KEYS) {
      if (!s(key)) return 'err_required';
    }
  }
  const symptoms = (form.ebola_symptoms as string[] | undefined) || [];
  if (!symptoms.length) return 'err_symptoms';
  return null;
}

export function buildScreeningPayload(form: Record<string, unknown>): Record<string, unknown> {
  const {
    ebola_country_of_departure, ebola_other_country_specify, ebola_point_of_entry,
    ebola_mode_of_transport, ebola_flight_number, ebola_vessel_number, ebola_vehicle_number,
    ebola_seat_number,
    ebola_visited_affected_countries, ebola_contact_confirmed_case,
    ebola_healthcare_lab_work, ebola_funeral_body_prep, ebola_contact_animals,
    ebola_bushmeat_handling, ebola_forest_cave_exposure,
    ebola_symptoms, temperature, age,
    phone_country_code: _phoneCc,
    ...rest
  } = form;

  const isYes = (v: unknown) => v === 'yes';
  const tempNum = temperature === '' || temperature == null ? null : Number(temperature);
  const ageNum = age === '' || age == null ? null : Number(age);
  const mode = String(ebola_mode_of_transport || '').toUpperCase();

  const transportNumber =
    mode === 'AIR' ? ebola_flight_number
      : mode === 'SHIP' ? ebola_vessel_number
        : mode === 'ROAD' ? ebola_vehicle_number
          : null;

  let countryOrigin: string | null = null;
  if (ebola_country_of_departure === 'other') {
    countryOrigin = (ebola_other_country_specify as string) || 'Other';
  } else if (ebola_country_of_departure) {
    const id = String(ebola_country_of_departure);
    countryOrigin = COUNTRY_TITLES[id] || id;
  }

  const combinedPhone = combinePhoneFields(form);

  const payload: Record<string, unknown> = {
    ...rest,
    phone_number: combinedPhone || rest.phone_number || null,
    age: ageNum != null && !Number.isNaN(ageNum) ? ageNum : null,
    temperature: tempNum != null && !Number.isNaN(tempNum) ? tempNum : null,
    id_type: rest.id_type || 'passport',
    ebola_screening_data: {
      mode_of_transport: mode || null,
      seat_number: ebola_seat_number || null,
      healthcare_lab_work: isYes(ebola_healthcare_lab_work),
      funeral_body_prep: isYes(ebola_funeral_body_prep),
      contact_animals: isYes(ebola_contact_animals),
      bushmeat_handling: isYes(ebola_bushmeat_handling),
      forest_cave_exposure: isYes(ebola_forest_cave_exposure),
      symptoms: ebola_symptoms || [],
    },
  };

  if (countryOrigin) payload.country_of_origin = countryOrigin;
  if (ebola_point_of_entry) payload.entry_city = ebola_point_of_entry;
  if (transportNumber) payload.transport_number = transportNumber;
  if (ebola_visited_affected_countries) {
    payload.visited_epidemic_area = isYes(ebola_visited_affected_countries);
  }
  if (ebola_contact_confirmed_case) {
    payload.contact_with_sick = isYes(ebola_contact_confirmed_case);
  }
  if (mode === 'AIR' || mode === 'SHIP' || mode === 'ROAD') {
    payload.entry_type = mode;
  }

  return Object.fromEntries(
    Object.entries(payload).map(([k, v]) => [k, v === '' ? null : v])
  );
}
